
import React, { useEffect, useRef } from "react";
import { formatDate } from "../utils/timelineUtils";

const labels = {
  CONFIRMED: "Confirmed",
  CHECKED_IN: "In house",
  PENDING: "Pending",
  CHECKED_OUT: "Checked out",
  CANCELLED: "Cancelled",
};

const statusClasses = {
  CONFIRMED: "occ-booking--confirmed",
  CHECKED_IN: "occ-booking--checked-in",
  PENDING: "occ-booking--pending",
  CHECKED_OUT: "occ-booking--checked-out",
  CANCELLED: "occ-booking--cancelled",
};

const paymentLabels = {
  PAID: "Paid",
  PARTIAL: "Partial",
  UNPAID: "Unpaid",
};

const loyaltyLabels = {
  VIP: "VIP",
  LOYALTY: "Loyalty",
  LOYAL: "Loyal",
  GOLD: "Gold",
  SILVER: "Silver",
  PLATINUM: "Platinum",
  PREMIUM: "Premium",
  REGULAR: "Regular",
};

function isEnabled(value) {
  return String(value ?? "").trim().toUpperCase() === "Y";
}

function formatBookingDateRange(checkIn, checkOut) {
  if (!checkIn || !checkOut) return "";

  const parseDate = (value) => {
    const match = String(value).match(
      /^(\d{4})-(\d{2})-(\d{2})/
    );

    if (match) {
      return new Date(
        Number(match[1]),
        Number(match[2]) - 1,
        Number(match[3])
      );
    }

    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  };

  const start = parseDate(checkIn);
  const end = parseDate(checkOut);

  if (!start || !end) return "";

  const formatShortDate = (date) =>
    date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
    });

  return `${formatShortDate(start)} – ${formatShortDate(end)}`;
}

function getIndicators(stay) {
  const loyaltyTier = String(stay.loyaltyTier ?? "VIP")
    .trim()
    .toUpperCase();

  const guestType = String(stay.guestType ?? "CORPORATE")
    .trim()
    .toUpperCase();

  const indicators = [];

  const add = (condition, icon, label, type) => {
    if (condition) indicators.push({ icon, label, type });
  };

  add(loyaltyTier === "VIP", "👑", "VIP", "vip");

  add(
    ["GOLD", "SILVER", "PLATINUM", "PREMIUM", "LOYALTY", "LOYAL"].includes(
      loyaltyTier
    ),
    "★",
    loyaltyLabels[loyaltyTier] || loyaltyTier,
    "loyalty"
  );

  add(guestType === "REPEAT", "↻", "Repeat", "repeat");
  add(guestType === "NEW", "✦", "New guest", "new");
  add(guestType === "CORPORATE", "▣", "Corporate", "corporate");

  add(
    isEnabled(stay.lateCheckIn),
    "◷",
    "Late check-in",
    "late"
  );

  const hasSpecialRequest = isEnabled(stay.specialRequest);
  const specialRequestRemarks = String(
    stay.specialRequestRemarks ?? ""
  ).trim();

  add(
    hasSpecialRequest,
    "☆",
    specialRequestRemarks
      ? `Special request: ${specialRequestRemarks}`
      : "Special request",
    "request"
  );

  return { loyaltyTier, indicators };
}

export default function TenantBooking({
  stay,
  left = 0,
  top = 4,
  width = 0,
  onClick,
  onDoubleClick,
  onContextMenu,
  onResize,
}) {
  const bookingRef = useRef(null);

  const status = String(stay?.status ?? "")
    .trim()
    .toUpperCase();

  const payment = String(stay?.paymentStatus ?? "")
    .trim()
    .toUpperCase();

  const name =
    stay?.tenantName ||
    stay?.guestName ||
    stay?.name ||
    "";

  const guestValue = stay?.guests;

  const guests =
    guestValue !== undefined &&
    guestValue !== null &&
    guestValue !== "" &&
    Number.isFinite(Number(guestValue)) &&
    Number(guestValue) >= 0
      ? Number(guestValue)
      : null;

  const stayId = stay?.id ?? stay?.stayId;
  const bookingWidth = Math.max(Number(width) || 0, 0);

  useEffect(() => {
    const element = bookingRef.current;
    if (!element || !stay) return;

    const track = element.parentElement;
    if (!track) return;

    const cardRect = element.getBoundingClientRect();
    const trackRect = track.getBoundingClientRect();

    console.log("[TenantBooking] Actual alignment", {
      checkIn: stay.checkIn,
      checkOut: stay.checkOut,
      calculatedLeft: left,
      calculatedWidth: bookingWidth,
      expectedRight: left + bookingWidth,
      actualLeft: cardRect.left - trackRect.left,
      actualWidth: cardRect.width,
      actualRight: cardRect.right - trackRect.left,
      trackWidth: trackRect.width,
    });
  }, [stay, left, bookingWidth]);

  if (!stay || bookingWidth <= 0) return null;

  const paymentClass =
    payment === "PAID"
      ? "occ-payment--paid"
      : payment === "PARTIAL"
        ? "occ-payment--partial"
        : payment === "UNPAID"
          ? "occ-payment--unpaid"
          : "";

  const paymentText = paymentLabels[payment] || payment;
  const statusText = labels[status] || status;

  const bookingDateRange = formatBookingDateRange(
    stay.checkIn,
    stay.checkOut
  );

  const { loyaltyTier, indicators } = getIndicators(stay);
  const isVip = loyaltyTier === "VIP";

  const visibleIndicators = indicators.filter(
    (indicator) => indicator.type !== "vip"
  );

  const title = [
    name,
    bookingDateRange ? `Stay: ${bookingDateRange}` : "",
    statusText,
    guests !== null
      ? `${guests} guest${guests !== 1 ? "s" : ""}`
      : "",
    paymentText ? `Payment: ${paymentText}` : "",
    loyaltyLabels[loyaltyTier] || loyaltyTier,
    ...indicators.map((indicator) => indicator.label),
    stay.checkIn ? formatDate(stay.checkIn) : "",
    stay.checkOut ? formatDate(stay.checkOut) : "",
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <div
      ref={bookingRef}
      className={[
        "occ-booking",
        statusClasses[status] || "",
        isVip ? "occ-booking--vip" : "",
      ]
        .filter(Boolean)
        .join(" ")}
      style={{
        position: "absolute",
        left,
        top,
        width: bookingWidth,
        height: 68,
        minHeight: 68,
        boxSizing: "border-box",
        zIndex: 2,
        overflow: "hidden",
        display: "flex",
        alignItems: "stretch",
      }}
      draggable={stayId != null}
      onDragStart={(event) => {
        if (stayId != null) {
          event.dataTransfer.setData(
            "text/occupancy-stay",
            String(stayId)
          );
          event.dataTransfer.effectAllowed = "move";
        }
      }}
      onClick={() => onClick?.(stay)}
      onDoubleClick={(event) => {
        event.stopPropagation();
        onDoubleClick?.(stay);
      }}
      onContextMenu={(event) => {
        event.preventDefault();
        onContextMenu?.(event, stay);
      }}
      title={title || undefined}
      aria-label={title || undefined}
    >
      <span
        className="occ-resize-handle left"
        title="Move check-in date"
        onClick={(event) => {
          event.stopPropagation();
          onResize?.(stay, "start", -1);
        }}
      >
        ‹
      </span>

      <div className="occ-booking__content">
        <div className="occ-booking__title">
          {name && (
            <b className="occ-booking__name" title={name}>
              {name}
            </b>
          )}

          {isVip && (
            <span
              className="occ-booking__loyalty occ-booking__loyalty--vip"
              title="VIP guest"
            >
              👑 VIP
            </span>
          )}
        </div>

        <div className="occ-booking__details">
          {bookingDateRange && (
            <span
              className="occ-booking__date-range"
              title={`Check-in: ${stay.checkIn || "—"} | Check-out: ${stay.checkOut || "—"}`}
            >
              {bookingDateRange}
            </span>
          )}

          {guests !== null && (
            <span className="occ-booking__guest-count">
              {guests} {guests === 1 ? "guest" : "guests"}
            </span>
          )}

          {statusText && (
            <span className="occ-booking__status">
              {statusText}
            </span>
          )}

          {paymentText && (
            <span
              className={`occ-booking__payment ${paymentClass}`}
              title={`Payment status: ${paymentText}`}
            >
              {paymentText}
            </span>
          )}
        </div>

        {visibleIndicators.length > 0 && (
          <div className="occ-booking__indicators">
            {visibleIndicators.map((indicator) => (
              <span
                key={`${indicator.type}-${indicator.label}`}
                className={[
                  "occ-booking__indicator",
                  `occ-booking__indicator--${indicator.type}`,
                ].join(" ")}
                title={indicator.label}
                aria-label={indicator.label}
              >
                <span aria-hidden="true">
                  {indicator.icon}
                </span>

                <span className="occ-booking__indicator-label">
                  {indicator.label}
                </span>
              </span>
            ))}
          </div>
        )}
      </div>

      <span
        className="occ-resize-handle right"
        title="Move check-out date"
        onClick={(event) => {
          event.stopPropagation();
          onResize?.(stay, "end", 1);
        }}
      >
        ›
      </span>
    </div>
  );
}
